import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
} from "reactstrap"

import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from "formik"
import * as Yup from "yup"

import CustomLabel from "components/Common/CustomLabel"
import { showAlert } from "store/reducers/alertReducer"
import {
  assignPermissionforTrialOperation,
  getAssignedPermissionforTrialOperation,
} from "store/actions/userAction"

const ErrorMessage = ({ name, errors, touched }) => {
  if (!errors[name] || !touched[name]) return null

  return <div className="text-danger fw-semibold mt-1">{errors[name]}</div>
}

const AddPermissions = ({ isOpen, toggle, user_uuid }) => {
  const [hovered, setHovered] = useState([])
  const [selected, setSelected] = useState(null)

  const initialValues = useMemo(() => {
    if (!selected) {
      return {
        shifts: [],
        stations: [],
      }
    }

    return {
      shifts: selected.trial_run.shifts,
      stations: selected.trial_run.stations,
    }
  }, [selected])

  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { work_shifts, stations } = useSelector(state => state.Common)
  const dispatch = useDispatch()

  const validationSchema = Yup.object().shape({
    shifts: Yup.array()
      .of(Yup.string())
      .min(1, t("Field is required"))
      .required(t("Field is required")),
    stations: Yup.array()
      .of(Yup.string())
      .min(1, t("Field is required"))
      .required(t("Field is required")),
  })

  useEffect(() => {
    dispatch(getAssignedPermissionforTrialOperation(user_uuid))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload

        if (statusCode === 200 && success) {
          setSelected(data)
        }
      })
      .catch(err => {
        console.log("err", err)
      })
  }, [])

  const buttonStyle = (selected, text, isHovered) => {
    return {
      flex: "1 1 100px",
      margin: "4px",
      padding: "10px 15px",
      border: `1px solid  ${
        selected || isHovered
          ? "#00778a"
          : text === "checkin"
          ? "green"
          : text === "checkout"
          ? "red"
          : "#99c1cd"
      }`,
      background: selected || isHovered ? "#00778a" : "white",
      color:
        selected || isHovered
          ? "white"
          : text === "checkout"
          ? "red"
          : text === "checkin"
          ? "green"
          : "#5d98a0",
      fontSize: "14px",
      cursor: "pointer",
      fontWeight: selected === text ? "bold" : "normal",
      transition: "all 0.3s ease",
      borderRadius: "5px",
      minWidth: "120px",
    }
  }

  const onSubmit = values => {
    const { shifts, stations } = values

    const obj = {
      permissions: {
        trial_run: {
          shifts,
          stations,
        },
      },
      user_uuid,
    }
    dispatch(assignPermissionforTrialOperation(obj))
      .then(action => {
        const { statusCode, success, message } = action?.payload

        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          toggle()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(err => {
        console.log("err", err)
      })
  }

  const { values, errors, touched, setFieldValue, handleSubmit, handleBlur } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit,
      enableReinitialize: true,
    })

  const handleFieldValue = (key, value) => {
    let prev = [...values[key]]
    const option = value
    const index = prev.includes(option)
    if (index) {
      prev = prev.filter(item => item !== option)
    } else {
      prev.push(option)
    }

    setFieldValue(key, prev)
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t("Assign Shifts & Stations")}</ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Shifts")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(work_shifts).map(option => (
                <button
                  key={option}
                  name="shifts"
                  onMouseEnter={() => setHovered([option])}
                  onMouseLeave={() => setHovered([])}
                  onBlur={handleBlur}
                  style={buttonStyle(
                    values.shifts.includes(option),
                    option,
                    hovered.includes(option)
                  )}
                  onClick={() => handleFieldValue("shifts", option)}
                >
                  {t(
                    work_shifts[option][language === "en" ? "name" : "name_ar"]
                  )}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="shifts" />
          </Col>

          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Select the stations")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(stations).map(option => (
                <button
                  key={option}
                  name="stations"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered([option])}
                  onMouseLeave={() => setHovered([])}
                  style={buttonStyle(
                    values.stations.includes(option),
                    option,
                    hovered.includes(option)
                  )}
                  onClick={() => handleFieldValue("stations", option)}
                >
                  {t(stations[option][language === "en" ? "name" : "name_ar"])}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="station" />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="main_btn disabled-button fit_content px-4"
            onClick={toggle}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="main_btn fit_content px-4"
            onClick={() => handleSubmit()}
          >
            {t("Save")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default AddPermissions
